import Tabs from "common/Tabs/Tabs";
import React, { FC, PropsWithChildren, useRef } from "react";
import { StylableButton } from "common/Buttons/Button";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import styled, { useTheme } from "styled-components";
import { Copy, Link2, Plus, WalletCards } from "lucide-react";
import Button from "common/Buttons/Button";
import { NEW_LOOK_ICON_STROKE, usePageIntro } from "common/NewLook";
import NewLookOverview from "features/affiliation/NewLook/NewLookOverview.tsx";
import NewLookReferralLinks from "features/affiliation/NewLook/NewLookReferralLinks.tsx";
import NewLookUsers from "features/affiliation/NewLook/NewLookUsers.tsx";
import NewLookEarnings from "features/affiliation/NewLook/NewLookEarnings.tsx";
import NewLookPayouts from "features/affiliation/NewLook/NewLookPayouts.tsx";
import CreateLinkModal from "features/affiliation/ReferralLinks/CreateLink/CreateLinkModal";
import Flex from "common/Boxes/Flex";
import useAffiliationPageWrapper from "features/affiliation/common/hooks/useAffiliationPageWrapper";
import { AffiliationContext } from "features/affiliation/Affiliation.context";
import CreatePayoutModal from "features/affiliation/Payouts/CreatePayoutModal";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { AffiliationTabs } from "features/affiliation/common/types";
import PageTabs from "common/Tabs/PageTabs.tsx";
import Overview from "features/affiliation/Overview/Overview.tsx";
import ReferralLinks from "features/affiliation/ReferralLinks/ReferralLinks.tsx";
import Users from "features/affiliation/Users/Users.tsx";
import Earnings from "features/affiliation/Earnings/Earnings.tsx";
import Payouts from "features/affiliation/Payouts/Payouts.tsx";
import { AFFILIATION_PATH } from "common/constants.ts";
import { generatePath } from "react-router-dom";
import { useDisablePageScroll } from "utils/hooks/misc.tsx";
import { LanguageNameSpaces, PayoutMethodSource } from "@shared/types";
import PayoutMethodsModal from "features/payouts/PayoutMethods/PayoutMethodsModal.tsx";

interface AffiliationPageProviderProps {
  tab?: AffiliationTabs;
  tabDisplayName?: string;
  onTabChange: (tabDisplayName: string) => void;
}

const AffiliationPageProvider: FC<
  PropsWithChildren<AffiliationPageProviderProps>
> = ({ tab, tabDisplayName, onTabChange }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { t: tPayouts } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const {
    isMobile,
    verySmallMobile,
    tabsAndButtonsInRow,
    isAdmin,
    hasFeatureFlag,
    getPromoCodeLoading,
    getReferralLinkLoading,
    isCreateLinkModalVisible,
    copyPromoCode,
    closeCreateLinkModal,
    tabItems,
    createOrCopyReferralLink,
    balance,
    payoutMethodsQuery,
    isCreatePayoutModalVisible,
    onCreatePayoutClick,
    closeCreatePayoutModal,
    hasPayoutMethod,
    isPayoutMethodsModalVisible,
    setIsPayoutMethodsModalVisible,
    showManagePayoutMethodsButton,
    isBottomNavigator,
  } = useAffiliationPageWrapper(tab);
  useDisablePageScroll(isBottomNavigator);
  const { isNewLook } = useTheme();
  // New look (desktop): tabs and page actions live in the page intro.
  const isNewLookIntro = isNewLook && !isBottomNavigator;

  const tabToComponent = isNewLook
    ? {
        [AffiliationTabs.OVERVIEW]: <NewLookOverview />,
        [AffiliationTabs.REFERRAL_LINKS]: <NewLookReferralLinks />,
        [AffiliationTabs.REFERRED_USERS]: (
          <NewLookUsers tab={AffiliationTabs.REFERRED_USERS} />
        ),
        [AffiliationTabs.EXISTING_USERS]: (
          <NewLookUsers tab={AffiliationTabs.EXISTING_USERS} />
        ),
        [AffiliationTabs.EARNINGS]: <NewLookEarnings />,
        [AffiliationTabs.PAYOUTS]: <NewLookPayouts />,
      }
    : {
        [AffiliationTabs.OVERVIEW]: <Overview />,
        [AffiliationTabs.REFERRAL_LINKS]: <ReferralLinks />,
        [AffiliationTabs.REFERRED_USERS]: (
          <Users tab={AffiliationTabs.REFERRED_USERS} />
        ),
        [AffiliationTabs.EXISTING_USERS]: (
          <Users tab={AffiliationTabs.EXISTING_USERS} />
        ),
        [AffiliationTabs.EARNINGS]: <Earnings />,
        [AffiliationTabs.PAYOUTS]: <Payouts />,
      };

  const yourPromoCodeButton = isAdmin && (
    <StyledButton
      onClick={copyPromoCode}
      $isMobile={isMobile}
      loading={getPromoCodeLoading}
    >
      {!verySmallMobile && <CopyOutlined />}
      {t("actions.promo_code")}
    </StyledButton>
  );

  const newReferralLinkButton = (
    <StyledButton
      onClick={createOrCopyReferralLink}
      $isMobile={isMobile}
      loading={getReferralLinkLoading}
    >
      {!verySmallMobile &&
        (hasFeatureFlag ? <PlusOutlined /> : <CopyOutlined />)}
      {t("actions.referral_link", {
        prefix: hasFeatureFlag ? t("actions.new") : t("actions.your"),
      })}
    </StyledButton>
  );

  const managePayoutMethodsButton = (
    <StyledButton
      onClick={() => setIsPayoutMethodsModalVisible(true)}
      $isMobile={isMobile}
    >
      {tPayouts("payouts.manage_payout_methods")}
    </StyledButton>
  );

  const buttons = (
    <ButtonsWrapper
      $tabsAndButtonsInRow={tabsAndButtonsInRow}
      $isBottomNavigator={isBottomNavigator}
    >
      {showManagePayoutMethodsButton ? (
        managePayoutMethodsButton
      ) : (
        <>
          {yourPromoCodeButton}
          {newReferralLinkButton}
        </>
      )}
    </ButtonsWrapper>
  );

  const newLookIconProps = { size: 16, strokeWidth: NEW_LOOK_ICON_STROKE };
  // The intro keeps its config between renders, so actions call the latest
  // handlers through refs.
  const handlersRef = useRef({ copyPromoCode, createOrCopyReferralLink });
  handlersRef.current = { copyPromoCode, createOrCopyReferralLink };
  // New look: on the payouts tab "Create payout" is the view's primary action.
  const getNewLookActions = (paneTab?: AffiliationTabs) =>
    showManagePayoutMethodsButton ? (
      <Button onClick={() => setIsPayoutMethodsModalVisible(true)}>
        <WalletCards {...newLookIconProps} />
        {tPayouts("payouts.manage_payout_methods")}
      </Button>
    ) : (
      <>
        {isAdmin && (
          <Button
            onClick={() => handlersRef.current.copyPromoCode()}
            loading={getPromoCodeLoading}
          >
            <Copy {...newLookIconProps} />
            {t("actions.promo_code")}
          </Button>
        )}
        <Button
          type={paneTab === AffiliationTabs.PAYOUTS ? "default" : "primary"}
          onClick={() => handlersRef.current.createOrCopyReferralLink()}
          loading={getReferralLinkLoading}
        >
          {hasFeatureFlag ? (
            <Plus {...newLookIconProps} />
          ) : (
            <Link2 {...newLookIconProps} />
          )}
          {t("actions.referral_link", {
            prefix: hasFeatureFlag ? t("actions.new") : t("actions.your"),
          })}
        </Button>
      </>
    );

  usePageIntro(
    isNewLookIntro
      ? {
          actions: getNewLookActions(tab),
          footer: (
            <NewLookTabs
              activeKey={tabDisplayName}
              onChange={onTabChange}
              items={tabItems}
            />
          ),
        }
      : {},
    [
      isNewLookIntro,
      tab,
      tabDisplayName,
      tabItems.length,
      showManagePayoutMethodsButton,
      isAdmin,
      hasFeatureFlag,
      getPromoCodeLoading,
      getReferralLinkLoading,
      t,
    ]
  );

  const itemsWithChildren = tabItems.map((tab) => {
    return {
      ...tab,
      children: isNewLook ? (
        <NewLookPane>
          <NewLookMobileActions>
            {getNewLookActions(tab.tab)}
          </NewLookMobileActions>
          {tabToComponent[tab.tab]}
        </NewLookPane>
      ) : (
        <div style={{ padding: 12, height: "100%", overflowY: "auto" }}>
          {buttons}
          {tabToComponent[tab.tab]}
        </div>
      ),
      link: generatePath(AFFILIATION_PATH, {
        tab: tab.tab,
      }),
    };
  });

  const content = isBottomNavigator ? (
    <PageTabs items={itemsWithChildren} />
  ) : (
    tab && tabToComponent[tab]
  );

  return (
    <AffiliationContext.Provider
      value={{
        onCreatePayoutClick,
        hasPayoutMethod,
        createOrCopyReferralLink,
        copyPromoCode,
        getPromoCodeLoading,
        getReferralLinkLoading,
        hasFeatureFlag,
        isAdmin,
        balance,
      }}
    >
      {!isBottomNavigator && !isNewLookIntro && (
        <Wrapper $tabsAndButtonsInRow={tabsAndButtonsInRow}>
          <StyledTabs
            activeKey={tabDisplayName}
            onChange={onTabChange}
            $isMobile={isMobile}
            items={tabItems}
          />
          {buttons}
        </Wrapper>
      )}
      {content}
      <CreateLinkModal
        isVisible={isCreateLinkModalVisible}
        closeModal={closeCreateLinkModal}
      />
      <CreatePayoutModal
        // New look: remount once the balance and methods load, so the form
        // starts from them instead of empty values.
        key={
          isNewLook
            ? `${balance}-${payoutMethodsQuery.data?.[0]?.payout_method_id}`
            : undefined
        }
        isVisible={isCreatePayoutModalVisible}
        closeModal={closeCreatePayoutModal}
        payoutMethods={payoutMethodsQuery.data}
        availableFunds={balance}
      />
      <PayoutMethodsModal
        open={isPayoutMethodsModalVisible}
        closeModal={() => setIsPayoutMethodsModalVisible(false)}
        payoutMethods={payoutMethodsQuery.data}
        source={PayoutMethodSource.AFFILIATION}
        loading={payoutMethodsQuery.isLoading}
      />
    </AffiliationContext.Provider>
  );
};

export default AffiliationPageProvider;

const Wrapper = styled.div<{ $tabsAndButtonsInRow: boolean }>`
  ${(p) =>
    p.$tabsAndButtonsInRow &&
    `
 display: flex;
 flex-wrap: wrap;
 justify-content: space-between;
 align-items: flex-start;
 `}
  overflow: hidden;
`;
const StyledTabs = styled(Tabs)<{ $isMobile: boolean }>`
  margin-right: 12px;
  margin-bottom: 12px;

  .ant-tabs-tab {
    padding: 4px 0 12px 0;
  }
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  .ant-tabs-tab + .ant-tabs-tab {
    margin: 0 0 0 24px;
  }
  ${(p) =>
    !p.$isMobile &&
    `
    width: fit-content;
  `}
`;
const ButtonsWrapper = styled(Flex)<{
  $tabsAndButtonsInRow: boolean;
  $isBottomNavigator?: boolean;
}>`
  justify-content: flex-end;
`;
const StyledButton = styled(StylableButton).attrs({
  type: "primary",
})<{ $isMobile?: boolean }>`
  float: right;
  &:not(:last-child) {
    margin-right: 8px;
  }

  ${(p) =>
    p.$isMobile &&
    `
 font-size: ${p.theme.typography.fontSizes.S};
 padding: 0 8px;
 `}
`;
const NewLookTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  /* The tabs sit in the toolbar row: no full-width hairline, just the ink bar. */
  .ant-tabs-nav::before {
    display: none;
  }
  .ant-tabs-tab {
    padding: 6px 0 8px;
    font-size: 14px;
  }
  .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 24px;
  }
  .ant-tabs-tab-btn {
    color: ${(p) => p.theme.colors.secondaryText};
  }
`;
const NewLookPane = styled.div`
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px 24px;
`;
const NewLookMobileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  .ant-btn {
    /* Share the row by label length; a label that can't fit wraps the row. */
    flex: 1 1 auto;
    min-width: fit-content;
    height: 40px;
    padding: 0 10px;
    gap: 6px;
    font-size: 13px;
  }
  .ant-btn > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
